import type { SpendReport, SpendTier } from '@/lib/adminApi';

/**
 * The Spend page's 6-month forecast (ClickUp 1amz0xe2evr): a pure function of a
 * base month and one scenario, no I/O. Per month t = 1..horizon:
 *
 *   signups_t  = signups_base * (1 + g)^t
 *   active_t   = signups_t * activation + active_(t-1) * retention
 *   user_cost  = active_t * (casual * c_casual + regular * c_regular + power * c_power)
 *   team_cost  = team_base * (1 + team_growth)^t
 *
 * with active_0 = the base month's active users and the per-user tier costs
 * taken from the base month. All rates are fractions (0.1 = 10%).
 */

export const HORIZON = 6;

export interface ForecastScenario {
  id: string;
  name: string;
  /** Monthly growth of signups. */
  signupGrowth: number;
  /** Share of a month's signups that become active in that month. */
  activation: number;
  /** Share of last month's active users who are active again. */
  retention: number;
  /** Share of active users in the `regular` tier (the `power` share is separate). */
  regularShare: number;
  powerShare: number;
  /** Monthly growth of the team's own spend. */
  teamGrowth: number;
}

export interface ForecastBase {
  /** 'YYYY-MM' of the month the numbers come from. */
  month: string;
  /** True when the base month is still running and its numbers were scaled up to a full month. */
  scaled: boolean;
  signups: number;
  activeUsers: number;
  /** Cost per active user per month, by tier. */
  tierCost: Record<SpendTier, number>;
  teamCost: number;
  /**
   * Months between the base month and the running month (0 when the base IS
   * the running month). The recurrence is iterated through them without
   * showing them: they already have real data.
   */
  gap?: number;
}

export interface ForecastMonth {
  month: string;
  signups: number;
  activeUsers: number;
  userCost: number;
  teamCost: number;
  totalCost: number;
}

export const DEFAULT_SCENARIOS: readonly ForecastScenario[] = [
  { id: 'as-is', name: 'As is', signupGrowth: 0.1, activation: 0.083, retention: 0.06, regularShare: 0.4, powerShare: 0.04, teamGrowth: 0 },
  { id: 'traffic', name: 'Traffic growth', signupGrowth: 0.25, activation: 0.1, retention: 0.1, regularShare: 0.4, powerShare: 0.04, teamGrowth: 0.1 },
  { id: 'regular', name: 'Regular users', signupGrowth: 0.15, activation: 0.12, retention: 0.35, regularShare: 0.55, powerShare: 0.1, teamGrowth: 0.1 },
  { id: 'aggressive', name: 'Aggressive', signupGrowth: 0.3, activation: 0.15, retention: 0.5, regularShare: 0.6, powerShare: 0.15, teamGrowth: 0.2 },
];

/** 'YYYY-MM' plus `n` months. */
export function addMonths(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number);
  const total = y * 12 + (m - 1) + n;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
}

export function forecast(
  base: ForecastBase,
  scenario: ForecastScenario,
  horizon: number = HORIZON,
): ForecastMonth[] {
  const casualShare = Math.max(0, 1 - scenario.regularShare - scenario.powerShare);
  const costPerActive =
    casualShare * base.tierCost.casual +
    scenario.regularShare * base.tierCost.regular +
    scenario.powerShare * base.tierCost.power;
  const out: ForecastMonth[] = [];
  const gap = base.gap ?? 0;
  let active = base.activeUsers;
  for (let t = 1; t <= gap + horizon; t += 1) {
    const signups = base.signups * (1 + scenario.signupGrowth) ** t;
    active = signups * scenario.activation + active * scenario.retention;
    const userCost = active * costPerActive;
    const teamCost = base.teamCost * (1 + scenario.teamGrowth) ** t;
    if (t <= gap) continue;
    out.push({
      month: addMonths(base.month, t),
      signups,
      activeUsers: active,
      userCost,
      teamCost,
      totalCost: userCost + teamCost,
    });
  }
  return out;
}

export function sumCost(months: readonly ForecastMonth[]): number {
  return months.reduce((acc, m) => acc + m.totalCost, 0);
}

const TEST_ACCOUNTS = 'test accounts';

/** The team's own cost in `month`: every team account, fixtures ('test accounts') excluded. */
export function teamCostOf(report: SpendReport, month: string): number {
  return report.team_monthly
    .filter((r) => r.month === month && r.account !== TEST_ACCOUNTS)
    .reduce((acc, r) => acc + r.cost_usd, 0);
}

/**
 * Pick the forecast base: the last FULL month when the window has one,
 * otherwise the running month scaled to its length (cost and signups by
 * days-in-month / days-elapsed). `null` when the window has no month with
 * active users to start from.
 */
export function deriveBase(report: SpendReport): ForecastBase | null {
  const asOf = new Date(report.as_of);
  const current = `${asOf.getUTCFullYear()}-${String(asOf.getUTCMonth() + 1).padStart(2, '0')}`;
  const daysInMonth = new Date(Date.UTC(asOf.getUTCFullYear(), asOf.getUTCMonth() + 1, 0)).getUTCDate();
  const elapsed = asOf.getUTCDate() - 1 + (asOf.getUTCHours() + asOf.getUTCMinutes() / 60) / 24;
  const partial = elapsed < daysInMonth;

  const usable = report.users_monthly.filter((r) => r.active_users > 0);
  const full = usable.filter((r) => r.month !== current || !partial);
  const row = full.length > 0 ? full[full.length - 1] : usable[usable.length - 1];
  if (!row) return null;

  const scaled = row.month === current && partial;
  const [cy, cm] = current.split('-').map(Number);
  const [by, bm] = row.month.split('-').map(Number);
  const gap = Math.max(0, cy * 12 + cm - (by * 12 + bm));
  const factor = scaled ? daysInMonth / Math.max(elapsed, 1) : 1;
  const tierCost: Record<SpendTier, number> = { casual: 0, regular: 0, power: 0 };
  for (const t of report.user_tiers) {
    // Not scaled: the user count already is, so scaling both would grow cost by factor².
    if (t.month === row.month) tierCost[t.tier] = t.cost_per_user;
  }
  return {
    month: row.month,
    scaled,
    gap,
    signups: row.signups * factor,
    activeUsers: row.active_users * factor,
    tierCost,
    teamCost: teamCostOf(report, row.month) * factor,
  };
}
